/**
 * How a person is NAMED on screen — the one display rule, for the API and
 * the web alike.
 *
 * principles.md #1: one rule per noun. A person's display name was computed
 * in at least five places on the Library rail alone (Shared with me, the
 * Favorites folder author, every list's uploader, the wiki author, the two
 * Share dialogs), each with its own join-and-trim, so the same person could
 * read "Ann Lee" on one tab and "Ann  Lee" on another (LB6-T4-R1/BF-05).
 * Every surface that names a person calls one of the functions below; the
 * guard `routes/v1/library/__tests__/one-person-naming-rule.test.ts` fails if
 * a hand-rolled copy comes back on the Library rail.
 *
 * WHO may be named, and from which rows, is the server's reader
 * (`nameUsersById` in packages/api/src/repositories/user.repository.ts);
 * this module only says how a name READS.
 */

import { z } from 'zod';

/** The parts of a person the display rule reads. */
export interface UserNameParts {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * "First Last" from whichever parts are set, each trimmed; `null` when
 * neither part has any text. Never the email, never the id.
 */
export function userPersonName(u: Pick<UserNameParts, 'firstName' | 'lastName'>): string | null {
  const name = [u.firstName, u.lastName]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0)
    .join(' ');
  return name.length > 0 ? name : null;
}

/**
 * THE display rule: "First Last" when either part is set, otherwise the
 * email. Never empty, never the id.
 */
export function userDisplayName(u: UserNameParts): string {
  return userPersonName(u) ?? u.email;
}

/**
 * The picker's label for a person: "First Last (email)" when they have a
 * name, otherwise the email alone — so two people with one name stay apart.
 */
export function userPickerLabel(u: UserNameParts): string {
  const name = userPersonName(u);
  return name === null ? u.email : `${name} (${u.email})`;
}

/**
 * A person NAMED by the server: the picker projection with one display name
 * (`userDisplayName`). What every response that names a person beside a row
 * carries (Shared with me's granter, a share's subject).
 */
export const NamedUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
});
export type NamedUser = z.infer<typeof NamedUserSchema>;
